/**
 * Reward history presentation helpers.
 *
 * Lives here rather than in @restaurant/core because it maps the
 * RewardTransaction contract (defined in this package) onto the display shape
 * the core components consume — core cannot import from viewmodels.
 */

import type { RewardHistoryEntry } from '@restaurant/core'
import type { RewardTransaction } from '../contracts/IRewardsPageService'

/**
 * Format a transaction timestamp for display, e.g. "Aug 3, 2026".
 * Falls back to the raw string when it is not a parseable date, so a bad row
 * shows something truthful instead of "Invalid Date".
 */
export function formatRewardHistoryDate(createdAt: string): string {
  const parsed = new Date(createdAt)
  if (isNaN(parsed.getTime())) return createdAt
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Map reward transactions to display-ready history entries, newest first.
 *
 * Sorting is done here on purpose: the backend's GET /reward_transactions
 * runs a plain select with no ORDER BY, so row order is not guaranteed. It
 * also ignores the `limit` query param, which is why callers cap rendering on
 * the client instead.
 */
export function toRewardHistoryEntries(transactions: RewardTransaction[]): RewardHistoryEntry[] {
  return [...(transactions ?? [])]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .map((t) => ({
      id: t.id,
      type: t.type,
      points: t.points,
      date: formatRewardHistoryDate(t.createdAt),
    }))
}
